import React from "react";
import {
  ESTADOS_CASO,
  type EstadoCaso,
  type PosesionResumen,
  type Asesor,
} from "@/lib/api";
import Toggle from "@/ui/Toggle";

// Tipos compartidos exportados para que Casos.tsx los importe
export type Vista = "activos" | "historico";
export type ClienteFiltro = { id: number; nombre: string };
// Preset de orden de la lista. "estancados"/"movidos" ordenan por última
// actividad; "recientes"/"antiguos" por fecha de creación.
export type OrdenCasos = "recientes" | "antiguos" | "estancados" | "movidos";
export type FiltroKpi = {
  marca?: string;
  modelo?: string;
  tipo_detalle?: string;
  categoria?: string;
  categoria_nombre?: string;
};

export type CasosFiltrosProps = {
  vista: Vista;
  estadoFilter: EstadoCaso | "todos";
  setEstadoFilter: (v: EstadoCaso | "todos") => void;
  q: string;
  setQ: (v: string) => void;
  searchRef?: React.RefObject<HTMLInputElement>;
  clienteFiltro: ClienteFiltro | null;
  setClienteFiltro: (v: ClienteFiltro | null) => void;
  agrupar: boolean;
  setAgrupar: (v: boolean) => void;
  esperaMiRespuesta: boolean;
  setEsperaMiRespuesta: (v: boolean) => void;
  soloCriticos: boolean;
  setSoloCriticos: (v: boolean) => void;
  proveedorPendiente: boolean;
  setProveedorPendiente: (v: boolean) => void;
  asesorFiltro: { id: number; nombre: string } | null;
  setAsesorFiltro: (v: { id: number; nombre: string } | null) => void;
  asesores: Asesor[];
  marcaFiltro: string | null;
  setMarcaFiltro: (v: string | null) => void;
  marcas: string[];
  filtroKpi: FiltroKpi | null;
  onClearFiltroKpi: () => void;
  orden: OrdenCasos;
  setOrden: (v: OrdenCasos) => void;
  total: number;
  loading: boolean;
  posesionFilter: "todos" | "mios" | "nosotros" | "cliente" | "proveedor";
  setPosesionFilter: (v: "todos" | "mios" | "nosotros" | "cliente" | "proveedor") => void;
  posesionResumen: PosesionResumen | null;
  nombreEmpresa: string;
  showPosesion: boolean;
  /**
   * Oculta la caja de búsqueda. En móvil los filtros viven dentro de un sheet y
   * el buscador se saca AFUERA, siempre visible: obligar a abrir/aplicar/cerrar
   * para teclear un folio era el viaje más caro de la pantalla.
   */
  ocultarBusqueda?: boolean;
};

export function etiquetaFiltroKpi(f: FiltroKpi): string {
  if (f.categoria) return f.categoria_nombre || f.categoria;
  const equipo = [f.marca, f.modelo].filter(Boolean).join(" · ");
  if (f.tipo_detalle) return equipo ? `${equipo} — ${f.tipo_detalle}` : f.tipo_detalle;
  return equipo;
}

/**
 * La caja de búsqueda de casos. Vive aparte porque se monta en DOS lugares: en
 * la tarjeta de filtros (escritorio) y suelta arriba de la lista (móvil).
 *
 * Toma renglón completo: cuando compartía fila con el toggle "Agrupar por
 * cliente" se encogía a la mitad y el placeholder se cortaba en "Buscar cliente
 * / folio", haciendo ver la búsqueda mucho más corta de lo que es.
 */
export function BuscadorCasos({
  q,
  setQ,
  searchRef,
}: {
  q: string;
  setQ: (v: string) => void;
  searchRef?: React.RefObject<HTMLInputElement>;
}) {
  return (
    <input
      ref={searchRef}
      type="search"
      placeholder="Buscar cliente, folio, TE, IMEI, equipo…  ( / )"
      aria-label="Buscar casos"
      className="input w-full sm:flex-1 sm:min-w-[260px]"
      value={q}
      onChange={(e) => setQ(e.target.value)}
    />
  );
}

export default function CasosFiltros({
  vista,
  estadoFilter,
  setEstadoFilter,
  q,
  setQ,
  searchRef,
  clienteFiltro,
  setClienteFiltro,
  agrupar,
  setAgrupar,
  esperaMiRespuesta,
  setEsperaMiRespuesta,
  soloCriticos,
  setSoloCriticos,
  proveedorPendiente,
  setProveedorPendiente,
  asesorFiltro,
  setAsesorFiltro,
  asesores,
  marcaFiltro,
  setMarcaFiltro,
  marcas,
  filtroKpi,
  onClearFiltroKpi,
  orden,
  setOrden,
  total,
  loading,
  posesionFilter,
  setPosesionFilter,
  posesionResumen,
  nombreEmpresa,
  showPosesion,
  ocultarBusqueda,
}: CasosFiltrosProps) {
  return (
    <div className="space-y-3">
      {/* Filtros principales */}
      <div className="card flex flex-wrap items-center gap-3">
        <select
          className="input w-full sm:w-auto sm:max-w-[220px]"
          value={estadoFilter}
          onChange={(e) => setEstadoFilter(e.target.value as EstadoCaso | "todos")}
        >
          <option value="todos">Todos los estados</option>
          {ESTADOS_CASO.filter((e) =>
            vista === "activos" ? e !== "Cerrado" : e === "Cerrado",
          ).map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>

        {filtroKpi && (
          <button
            type="button"
            onClick={onClearFiltroKpi}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/40 hover:bg-violet-500/25 transition max-w-full"
            aria-label={`Quitar el filtro que llegó desde las estadísticas: ${etiquetaFiltroKpi(filtroKpi)}`}
          >
            <span className="truncate">Desde estadísticas: {etiquetaFiltroKpi(filtroKpi)}</span>
            <span aria-hidden className="text-violet-400 shrink-0">✕</span>
          </button>
        )}

        {clienteFiltro && (
          <button
            type="button"
            onClick={() => setClienteFiltro(null)}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-accent-500/15 text-accent-300 border border-accent-500/40 hover:bg-accent-500/25 transition max-w-full"
            aria-label={`Quitar el filtro de cliente: ${clienteFiltro.nombre}`}
          >
            <span className="truncate">Cliente: {clienteFiltro.nombre}</span>
            <span aria-hidden className="text-accent-400 shrink-0">✕</span>
          </button>
        )}

        {!ocultarBusqueda && (
          <BuscadorCasos q={q} setQ={setQ} searchRef={searchRef} />
        )}

        <Toggle
          checked={agrupar}
          onChange={setAgrupar}
          label="Agrupar por cliente"
        />

        <Toggle
          checked={esperaMiRespuesta}
          onChange={setEsperaMiRespuesta}
          label="Solo los que esperan mi respuesta"
        />

        <Toggle
          checked={soloCriticos}
          onChange={setSoloCriticos}
          label="Solo con equipo crítico"
        />

        <Toggle
          checked={proveedorPendiente}
          onChange={setProveedorPendiente}
          label="El proveedor ya contestó"
        />

        {asesores.length > 0 && (
          <select
            className="input w-full sm:w-auto sm:max-w-[220px]"
            value={asesorFiltro?.id ?? ""}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : null;
              setAsesorFiltro(
                id ? (asesores.find((a) => a.id === id) ?? null) : null,
              );
            }}
            aria-label="Asesor comercial"
          >
            <option value="">Asesor comercial: todos</option>
            {asesores.map((a) => (
              <option key={a.id} value={a.id}>{a.nombre}</option>
            ))}
          </select>
        )}

        {marcas.length > 0 && (
          <select
            className="input w-full sm:w-auto sm:max-w-[200px]"
            value={marcaFiltro ?? ""}
            onChange={(e) => setMarcaFiltro(e.target.value || null)}
            aria-label="Marca"
          >
            <option value="">Marca: todas</option>
            {marcas.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        )}

        {/* Ordenar la lista. "Más estancados" sube los casos con más días sin
            movimiento (por última actividad); en Histórico solo aplica el orden
            por fecha de creación, porque los cerrados no tienen actividad viva. */}
        <select
          className="input w-full sm:w-auto sm:max-w-[240px]"
          value={orden}
          onChange={(e) => setOrden(e.target.value as OrdenCasos)}
          aria-label="Ordenar por"
        >
          <option value="recientes">Ordenar: recién creados</option>
          <option value="antiguos">Ordenar: más antiguos</option>
          {vista === "activos" && (
            <option value="estancados">Ordenar: más estancados</option>
          )}
          {vista === "activos" && (
            <option value="movidos">Ordenar: movidos hace poco</option>
          )}
        </select>

        <span className="text-xs text-zinc-500 ml-auto">
          {loading ? "Cargando..." : `${total} caso${total === 1 ? "" : "s"}`}
        </span>
      </div>

      {/* Chips de posesión (solo en vista activos cuando hay resumen) */}
      {showPosesion && posesionResumen && (
        <div className="card flex flex-wrap items-center gap-2 text-xs">
          <span className="text-[10px] uppercase tracking-wide text-zinc-500">
            Posesión
          </span>
          {(
            [
              ["todos", "Todos", null],
              ["mios", "Míos", posesionResumen.mios],
              ["nosotros", nombreEmpresa, posesionResumen.nosotros],
              ["cliente", "Cliente", posesionResumen.cliente],
              ["proveedor", "Proveedor", posesionResumen.proveedor],
            ] as const
          ).map(([v, label, count]) => (
            <button
              key={v}
              type="button"
              onClick={() => setPosesionFilter(v)}
              className={`px-2.5 py-1 rounded-full border transition ${
                posesionFilter === v
                  ? "bg-accent-500/15 text-accent-300 border-accent-500/40"
                  : "border-ink-600 text-zinc-400 hover:text-white"
              }`}
            >
              {label}
              {count != null ? ` (${count})` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
